import { StrictMode, useEffect } from "react";
import { createRoot } from "react-dom/client";
import { BrowserRouter } from "react-router-dom";
import { ConfigProvider, App as AntdApp } from "antd";
import zhTW from "antd/locale/zh_TW";
import App from "./App.tsx";
import "./index.css";
import "./styles/keyboard-fix.css"; // iOS Safari 键盘适配
import "@ifa/ui";
import { MessageBridge } from "./components/MessageBridge";
import {
  TenantThemeProvider,
  createTenantThemeRuntime,
  getBrandAssets,
} from "@ifa/ui";
import {
  setSelectMetadataProvider,
  setCountryProvider,
  setBrandAssetsProvider,
  useMetadataStore,
  useCountryStore,
  useConfigStore,
  initSSOClient,
  ssoLogout,
  config,
  setTokenProvider,
  setTenantCodeProvider,
  setUnauthorizedHandler,
  type ExchangeTokenResult,
} from "@ifa/utils";


// 当前应用标识
const APP_CODE = "ADVISOR_SMART_PORTAL";

// localStorage 工具函数
const getToken = () => localStorage.getItem("token");
const getTenantCode = () => localStorage.getItem("tenantCode");
const saveTenantCode = (tenantCode: string) => {
  localStorage.setItem("tenantCode", tenantCode);
};
const clearAuth = () => {
  localStorage.removeItem("token");
  localStorage.removeItem("uid");
  localStorage.removeItem("tenantCode");
};
const saveAuth = (result: ExchangeTokenResult) => {
  localStorage.setItem("token", result.token);
  localStorage.setItem("uid", String(result.uid));
};

// 获取当前页面 URL（用于 SSO 回调）
// 重要：必须返回当前真实的 URL，而不是固定值
const getCurrentUrl = () => window.location.href;

// 初始化 SSO 客户端
initSSOClient({
  authUrl: config.authUrl,
  app: APP_CODE,
  getToken,
  saveAuth,
  clearAuth,
  saveTenantCode,
  getCurrentUrl,
});

// 注入 token 获取方式
setTokenProvider(() => getToken() || "");

// 注入租户代码获取方式
setTenantCodeProvider(() => getTenantCode() || "");

// 注入未授权处理：使用 SSO 登出
setUnauthorizedHandler(() => {
  ssoLogout();
});

// 初始化全局提供者
setSelectMetadataProvider(() => useMetadataStore.getState().selectMetadataList);
setCountryProvider(() => useCountryStore.getState().allCountries);

// 注入品牌资源提供者
setBrandAssetsProvider(getBrandAssets);

// 初始化全局配置（基于当前域名）
useConfigStore.getState().syncRuntimeTenantWithCurrentDomain(getTenantCode());

function ThemeBridge({ children }: { children: React.ReactNode }) {
  const tenantId = useConfigStore((state) => state.tenantProfile?.tenantCode ?? "default");
  const brandId = useConfigStore((state) => state.brandId);
  const layoutType = useConfigStore((state) => state.layoutType);
  const runtime = createTenantThemeRuntime({
    tenantId,
    brandId,
    appId: "user",
    layoutType,
  });
  runtime.antdTheme = {
    ...runtime.antdTheme,
    token: {
      ...runtime.antdTheme.token,
      zIndexPopupBase: 100002,
    },
  };

  return <TenantThemeProvider runtime={runtime}>{children}</TenantThemeProvider>;
}

createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <BrowserRouter>
      <ConfigProvider locale={zhTW}>
        <ThemeBridge>
          <AntdApp>
            <MessageBridge />
            <App />
          </AntdApp>
        </ThemeBridge>
      </ConfigProvider>
    </BrowserRouter>
  </StrictMode>
);
